import is from '@sindresorhus/is';
import Maybe from 'true-myth/maybe';
import Result from 'true-myth/result';
import { z } from 'zod';

import { HTTPError } from './http-error';
import { HTTPErrorWithResponseBody } from './http-error-with-response-body';
import type { HTTPMethod } from './http-method';
import { NetworkError } from './network-error';
import { isNetworkErrorReportClaimed, takeNetworkErrorReportSlot } from './network-error-report';

export type RequestOptions = {
    readonly body?: BodyInit;
    readonly credentials?: RequestCredentials;
    readonly headers?: Record<string, string>;
    readonly searchParams?: Record<string, string | number | boolean | undefined> | URLSearchParams | string;
    readonly signal?: AbortSignal;
    readonly timeout?: number;
};

export type Pathname = `/${string}`;

type InternalFetchOptions = {
    readonly expectedStatusCodes?: readonly number[];
    readonly method: HTTPMethod;
    readonly payload?: unknown;
    readonly requestOptions: RequestOptions;
    readonly url: URL;
};

export type FetchRawResult = {
    readonly data: unknown;
    readonly status: number;
};

export type HttpFetcherOptions = {
    readonly baseUrl?: URL;
    readonly requestHeaders?: RequestHeaders;
};

export type ErrorReportContext = Readonly<Record<string, unknown>>;

export type ErrorReporter = {
    readonly reportError: (message: string, error: Error, context?: ErrorReportContext) => void;
};

type HttpFetcherDependencies = {
    readonly errorReporter: ErrorReporter;
    readonly fetch: typeof globalThis.fetch;
    readonly onRateLimited?: () => void;
};

type ParametersWithoutPayloadBase = {
    readonly expectedStatusCodes?: readonly number[];
    readonly requestOptions?: RequestOptions;
};

export type ParametersWithUrl = {
    readonly url: URL;
};

export type ParametersWithPathname = {
    readonly pathname: Pathname;
};

type ParametersWithoutPayload<T extends HttpFetcherOptions> = T['baseUrl'] extends URL
    ? ParametersWithoutPayloadBase & ParametersWithPathname
    : ParametersWithoutPayloadBase & ParametersWithUrl;

type ParametersWithJsonPayload<T extends HttpFetcherOptions> = ParametersWithoutPayload<T> & {
    readonly payload: unknown;
};

export type RequestHeaders = Record<string, string>;

export type AuthorizationTokenSource = {
    readonly getAuthorizationToken: (options?: { readonly rejectedToken?: string }) => Promise<Maybe<string>>;
    readonly onUnauthorized: () => void;
};

const toAuthorizationHeaders = (token: Maybe<string>): RequestHeaders =>
    token.mapOr<RequestHeaders>({}, (value) => ({ Authorization: `Bearer ${value}` }));

export const resolveAuthorizationHeaders = async (
    authorization: Maybe<AuthorizationTokenSource>,
): Promise<RequestHeaders> =>
    authorization.isNothing ? {} : toAuthorizationHeaders(await authorization.value.getAuthorizationToken());

export const isParametersWithUrl = (
    parameters: ParametersWithPathname | ParametersWithUrl,
): parameters is ParametersWithUrl => {
    return 'url' in parameters && parameters.url instanceof URL;
};

type ErrorHandler = (error: HTTPError) => void;

export type HttpFetcherError = Error & { readonly errorCode?: string; readonly message?: string };

const backendErrorsSchema = z.object({
    errors: z
        .object({
            errorCode: z.string(),
            message: z.string(),
        })
        .array()
        .min(1),
});

const mapErrorToHttpFetcherError = (value: unknown, error: Error): HttpFetcherError => {
    const backendErrors = backendErrorsSchema.safeParse(value);

    if (backendErrors.success) {
        const errorCode = backendErrors.data.errors[0]?.errorCode ?? '';
        const errorMessage = backendErrors.data.errors[0]?.message ?? error.message;

        const message = `${errorCode}: ${errorMessage}`;

        return Object.assign(error, { errorCode, message });
    }

    return error;
};

export interface HttpFetcher<T extends HttpFetcherOptions = HttpFetcherOptions> {
    readonly addHttpErrorHandler: (errorHandler: ErrorHandler) => void;
    readonly delete: (options: ParametersWithoutPayload<T>) => Promise<Result<unknown, HttpFetcherError>>;
    readonly deleteJson: (options: ParametersWithJsonPayload<T>) => Promise<Result<unknown, HttpFetcherError>>;
    readonly get: (options: ParametersWithoutPayload<T>) => Promise<Result<unknown, HttpFetcherError>>;
    readonly getWithStatus: (options: ParametersWithoutPayload<T>) => Promise<Result<FetchRawResult, HttpFetcherError>>;
    readonly patchJson: (options: ParametersWithJsonPayload<T>) => Promise<Result<unknown, HttpFetcherError>>;
    readonly post: (options: ParametersWithoutPayload<T>) => Promise<Result<unknown, HttpFetcherError>>;
    readonly postJson: (options: ParametersWithJsonPayload<T>) => Promise<Result<unknown, HttpFetcherError>>;
    readonly put: (options: ParametersWithoutPayload<T>) => Promise<Result<unknown, HttpFetcherError>>;
    readonly putJson: (options: ParametersWithJsonPayload<T>) => Promise<Result<unknown, HttpFetcherError>>;
    set authorization(source: AuthorizationTokenSource);
    set requestHeaders(headers: RequestHeaders);
}

const applySearchParams = (url: URL, searchParams: RequestOptions['searchParams']): URL => {
    if (is.nullOrUndefined(searchParams)) {
        return url;
    }

    const result = new URL(url.toString());

    if (is.plainObject(searchParams)) {
        for (const [key, value] of Object.entries(searchParams)) {
            if (!is.undefined(value)) {
                result.searchParams.append(key, String(value));
            }
        }
    } else {
        for (const [key, value] of new URLSearchParams(searchParams)) {
            result.searchParams.append(key, value);
        }
    }

    return result;
};

const isAbortError = (error: unknown): boolean => error instanceof Error && error.name === 'AbortError';

// `fetch` rejects with a TypeError when the request never reached a response, and so does reading
// a successful response's body when the connection drops mid-transfer — both are retryable network
// failures. An abort is a DOMException and a malformed body a SyntaxError, and a TypeError thrown
// by a rate-limit or http error handler is a plain bug, so only these two operations are wrapped.
const classifyNetworkFailure = async <T>(method: HTTPMethod, url: URL, operation: () => Promise<T>): Promise<T> => {
    try {
        return await operation();
    } catch (error: unknown) {
        throw error instanceof TypeError ? new NetworkError(method, url.toString(), error) : error;
    }
};

// Waiting for a token honours the request's own abort signal without cancelling a renewal that
// other requests share.
const abortable = <T>(promise: Promise<T>, signal: AbortSignal | undefined): Promise<T> => {
    if (is.undefined(signal)) {
        return promise;
    }

    if (signal.aborted) {
        return Promise.reject(signal.reason);
    }

    return new Promise<T>((resolve, reject) => {
        const onAbort = (): void => reject(signal.reason);
        signal.addEventListener('abort', onAbort, { once: true });
        promise.then(resolve, reject).finally(() => signal.removeEventListener('abort', onAbort));
    });
};

// A 401 usually means the token was revoked or expired while in flight, so the request is replayed
// once with a renewed token. When renewal could not produce a different token, for example during an
// identity provider outage, the 401 is returned as is and the session stays intact.
const sendWithAuthorizationReplay = async (
    send: (token: Maybe<string>) => Promise<Response>,
    authorization: Maybe<AuthorizationTokenSource>,
    signal: AbortSignal | undefined,
): Promise<Response> => {
    if (authorization.isNothing) {
        return send(Maybe.nothing());
    }

    const { getAuthorizationToken, onUnauthorized } = authorization.value;
    const token = await abortable(getAuthorizationToken(), signal);
    const response = await send(token);

    if (response.status !== 401 || token.isNothing) {
        return response;
    }

    const renewedToken = await abortable(getAuthorizationToken({ rejectedToken: token.value }), signal);

    if (renewedToken.isNothing || renewedToken.value === token.value) {
        return response;
    }

    const replayedResponse = await send(renewedToken);

    if (replayedResponse.status === 401) {
        onUnauthorized();
    }

    return replayedResponse;
};

const toErrorSuffix = (error: unknown): string => (is.error(error) ? `: ${error.message}` : '');

let rateLimitFailureCount = 0;
let rateLimitResetAt = 0;

const waitForRateLimit = async (): Promise<void> => {
    while (rateLimitResetAt > Date.now()) {
        await new Promise((resolve) => setTimeout(resolve, rateLimitResetAt - Date.now()));
    }
};

const createSignal = (timeout: number | undefined, signal: AbortSignal | undefined): AbortSignal | undefined => {
    if (is.undefined(timeout)) {
        return signal;
    }

    const timeoutSignal = AbortSignal.timeout(timeout);

    return is.undefined(signal) ? timeoutSignal : AbortSignal.any([signal, timeoutSignal]);
};

export const createHttpFetcher = <T extends HttpFetcherOptions>(
    { errorReporter, fetch: fetchFn, onRateLimited }: HttpFetcherDependencies,
    options?: T,
): HttpFetcher<T> => {
    const { baseUrl, requestHeaders: initialRequestHeaders } = options ?? {};
    let requestHeaders: Maybe<RequestHeaders> = Maybe.nothing();
    let authorization: Maybe<AuthorizationTokenSource> = Maybe.nothing();
    const httpErrorHandlers: ErrorHandler[] = [];

    const determineUrlToFetch = (urlParameters: ParametersWithPathname | ParametersWithUrl): URL => {
        if (isParametersWithUrl(urlParameters)) {
            return urlParameters.url;
        }

        const basePath = baseUrl?.pathname.replace(/\/+$/, '');
        const extendedPath = urlParameters.pathname.replace(/^\/+/, '');

        return new URL(`${basePath}/${extendedPath}`, baseUrl);
    };

    const fetchRaw = async (options: InternalFetchOptions): Promise<Result<FetchRawResult, HttpFetcherError>> => {
        const { expectedStatusCodes = [], method, payload, requestOptions, url } = options;
        const headers: Record<string, string> = requestHeaders.mapOrElse(
            () => ({ ...initialRequestHeaders, ...requestOptions.headers }),
            (defaultHeaders) => ({ ...initialRequestHeaders, ...defaultHeaders, ...requestOptions.headers }),
        );

        const fetchUrl = applySearchParams(url, requestOptions.searchParams);
        const signal = createSignal(requestOptions.timeout, requestOptions.signal);

        const body = is.undefined(payload) ? requestOptions.body : JSON.stringify(payload);
        let fetchInit: RequestInit = {};

        const send = (token: Maybe<string>): Promise<Response> => {
            const requestHeadersWithAuthorization = {
                ...toAuthorizationHeaders(token),
                ...headers,
                ...(is.undefined(payload) ? {} : { 'content-type': 'application/json' }),
            };
            fetchInit = {
                body,
                credentials: requestOptions.credentials,
                headers: requestHeadersWithAuthorization,
                method,
                signal,
            };

            return classifyNetworkFailure(method, fetchUrl, () => fetchFn(fetchUrl, fetchInit));
        };

        try {
            await waitForRateLimit();
            const requestStartedAt = Date.now();
            const response = await sendWithAuthorizationReplay(send, authorization, signal);

            if (response.status === 429) {
                onRateLimited?.();
                rateLimitResetAt = Math.max(
                    rateLimitResetAt,
                    Date.now() + Math.min(1000 * 2 ** rateLimitFailureCount, 10_000),
                );
                rateLimitFailureCount += 1;
            } else if (requestStartedAt >= rateLimitResetAt) {
                rateLimitFailureCount = 0;
                rateLimitResetAt = 0;
            }

            if (!response.ok) {
                const httpError = new HTTPError(response, new Request(fetchUrl, fetchInit));

                if (!expectedStatusCodes.includes(response.status)) {
                    errorReporter.reportError(httpError.message, httpError);
                }

                for (const httpErrorHandler of httpErrorHandlers) {
                    httpErrorHandler(httpError);
                }

                try {
                    const responseJson = await response.json();

                    return Result.err(
                        mapErrorToHttpFetcherError(
                            responseJson,
                            new HTTPErrorWithResponseBody(httpError, responseJson),
                        ),
                    );
                } catch (_: unknown) {
                    return Result.err(httpError);
                }
            }

            const contentType = response.headers.get('content-type');
            const isJson = /^application\/(?:json|[^;\s]+\+json)(?=[;\s]|$)/i.test(contentType ?? '');
            const data = await classifyNetworkFailure(method, fetchUrl, async () =>
                isJson ? response.json() : response.text(),
            );

            return Result.ok({ data, status: response.status });
        } catch (caughtError: unknown) {
            if (!(caughtError instanceof NetworkError)) {
                const error = new Error(`Could not fetch ${method} ${fetchUrl}${toErrorSuffix(caughtError)}`, {
                    cause: caughtError,
                });

                if (!isAbortError(caughtError)) {
                    errorReporter.reportError(error.message, error);
                }

                return Result.err(error);
            }

            // A network failure is retryable, so its report belongs to whoever owns the retries.
            // They claim it in a promise continuation (a microtask), so this macrotask runs
            // afterwards and reports only what nobody took responsibility for. This relies on
            // a caller not awaiting anything between this result and passing the error on.
            setTimeout(() => {
                if (!isNetworkErrorReportClaimed(caughtError) && takeNetworkErrorReportSlot(caughtError, 'attempt')) {
                    errorReporter.reportError(caughtError.message, caughtError);
                }
            }, 0);

            return Result.err(caughtError);
        }
    };

    const fetch = async (options: InternalFetchOptions): Promise<Result<unknown, HttpFetcherError>> => {
        const result = await fetchRaw(options);

        if (result.isOk) {
            return Result.ok(result.value.data);
        }

        return result;
    };

    return {
        addHttpErrorHandler(httpErrorHandler) {
            httpErrorHandlers.push(httpErrorHandler);
        },
        async delete(parameters) {
            const { expectedStatusCodes, requestOptions = {} } = parameters;

            return fetch({
                expectedStatusCodes,
                method: 'DELETE',
                requestOptions,
                url: determineUrlToFetch(parameters),
            });
        },
        async deleteJson(parameters) {
            const { expectedStatusCodes, payload, requestOptions = {} } = parameters;

            return fetch({
                expectedStatusCodes,
                method: 'DELETE',
                payload,
                requestOptions,
                url: determineUrlToFetch(parameters),
            });
        },
        async get(parameters) {
            const { expectedStatusCodes, requestOptions = {} } = parameters;

            return fetch({ expectedStatusCodes, method: 'GET', requestOptions, url: determineUrlToFetch(parameters) });
        },
        async getWithStatus(parameters) {
            const { expectedStatusCodes, requestOptions = {} } = parameters;

            return fetchRaw({
                expectedStatusCodes,
                method: 'GET',
                requestOptions,
                url: determineUrlToFetch(parameters),
            });
        },
        async patchJson(parameters) {
            const { expectedStatusCodes, payload, requestOptions = {} } = parameters;

            return fetch({
                expectedStatusCodes,
                method: 'PATCH',
                payload,
                requestOptions,
                url: determineUrlToFetch(parameters),
            });
        },
        async post(parameters) {
            const { expectedStatusCodes, requestOptions = {} } = parameters;

            return fetch({ expectedStatusCodes, method: 'POST', requestOptions, url: determineUrlToFetch(parameters) });
        },
        async postJson(parameters) {
            const { expectedStatusCodes, payload, requestOptions = {} } = parameters;

            return fetch({
                expectedStatusCodes,
                method: 'POST',
                payload,
                requestOptions,
                url: determineUrlToFetch(parameters),
            });
        },
        async put(parameters) {
            const { expectedStatusCodes, requestOptions = {} } = parameters;

            return fetch({ expectedStatusCodes, method: 'PUT', requestOptions, url: determineUrlToFetch(parameters) });
        },
        async putJson(parameters) {
            const { expectedStatusCodes, payload, requestOptions = {} } = parameters;

            return fetch({
                expectedStatusCodes,
                method: 'PUT',
                payload,
                requestOptions,
                url: determineUrlToFetch(parameters),
            });
        },
        set authorization(source: AuthorizationTokenSource) {
            authorization = Maybe.just(source);
        },
        set requestHeaders(_requestHeaders: RequestHeaders) {
            requestHeaders = requestHeaders.mapOrElse(
                () => Maybe.of(_requestHeaders),
                (headers) =>
                    Maybe.of<RequestHeaders>({
                        ...headers,
                        ..._requestHeaders,
                    }),
            );
        },
    };
};
